import { rawAttr } from "@wiki/core";
import type { CellAlignment } from "@wiki/core";
import { Fragment, type Node as ProseMirrorNode } from "prosemirror-model";
import { TextSelection, type Command, type EditorState } from "prosemirror-state";
import {
  CellSelection,
  TableMap,
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumn,
  deleteRow,
  deleteTable,
  goToNextCell,
  isInTable,
  mergeCells,
  moveTableColumn,
  moveTableRow,
  selectedRect,
  setCellAttr,
  splitCell,
  toggleHeaderColumn,
  toggleHeaderRow,
} from "prosemirror-tables";

import { findParentOfType, nodes } from "../schema";

export {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumn,
  deleteRow,
  deleteTable,
  goToNextCell,
  isInTable,
  mergeCells,
  splitCell,
  toggleHeaderColumn,
  toggleHeaderRow,
};

export function setCellAlignment(alignment: CellAlignment | null): Command {
  return setCellAttr("alignment", alignment);
}

/** The cell background rides in the cell's `marks` attribute (§12). */
export function setCellBackground(color: string | null): Command {
  return setCellAttr("marks", color === null ? undefined : [{ type: "background", color }]);
}

export function cellBackground(node: ProseMirrorNode): string | null {
  const marks = rawAttr(node, "marks");
  if (!Array.isArray(marks)) return null;
  for (const entry of marks as { type?: unknown; color?: unknown }[]) {
    if (entry.type === "background" && typeof entry.color === "string") return entry.color;
  }
  return null;
}

export function tableAt(state: EditorState): { node: ProseMirrorNode; pos: number } | null {
  const found = findParentOfType(state.selection.$from, nodes.table);
  return found ? { node: found.node, pos: found.pos } : null;
}

/** The table toolbar's full-width toggle (§12, `layout`). */
export const toggleTableFullWidth: Command = (state, dispatch) => {
  const table = tableAt(state);
  if (!table) return false;
  const next = rawAttr(table.node, "layout") === "full-width" ? null : "full-width";
  if (dispatch) dispatch(state.tr.setNodeAttribute(table.pos, "layout", next));
  return true;
};

/** "Distribute columns": drop every stored `colwidth`. */
export const distributeColumns: Command = (state, dispatch) => {
  const table = tableAt(state);
  if (!table) return false;
  if (dispatch) {
    const tr = state.tr;
    table.node.descendants((node, offset) => {
      if (node.type === nodes.tableCell || node.type === nodes.tableHeader) {
        tr.setNodeAttribute(table.pos + 1 + offset, "colwidth", null);
        return false;
      }
      return true;
    });
    dispatch(tr);
  }
  return true;
};

/** Column resize handles persist their result here (§12, `colwidth`). */
export function setColumnWidth(tablePos: number, column: number, width: number | null): Command {
  return (state, dispatch) => {
    const table = state.doc.nodeAt(tablePos);
    if (table?.type !== nodes.table) return false;
    const map = TableMap.get(table);
    if (column < 0 || column >= map.width) return false;
    if (dispatch) {
      const tr = state.tr;
      for (let row = 0; row < map.height; row += 1) {
        const cellPos = map.map[row * map.width + column];
        if (cellPos === undefined) continue;
        const cell = table.nodeAt(cellPos);
        if (!cell) continue;
        // A cell spanning several columns is sized by its first column only.
        if (map.map[row * map.width + column - 1] === cellPos) continue;
        tr.setNodeMarkup(tablePos + 1 + cellPos, undefined, { ...cell.attrs, colwidth: width });
      }
      dispatch(tr);
    }
    return true;
  };
}

function rowText(row: ProseMirrorNode, column: number): string {
  const cell = column < row.childCount ? row.child(column) : null;
  return cell ? cell.textContent.trim() : "";
}

/** Sort body rows by a column, ascending or descending (§12). */
export function sortColumn(column: number, direction: "asc" | "desc"): Command {
  return (state, dispatch) => {
    const table = tableAt(state);
    if (!table) return false;
    const rows: ProseMirrorNode[] = [];
    table.node.forEach((row) => rows.push(row));
    const headerRows = rows.filter((row) => row.firstChild?.type === nodes.tableHeader).length;
    const head = rows.slice(0, headerRows);
    const body = rows.slice(headerRows);
    if (body.length < 2) return false;
    const sorted = [...body].sort((a, b) => {
      const left = rowText(a, column);
      const right = rowText(b, column);
      const numbers = Number(left) - Number(right);
      const compared =
        left !== "" && right !== "" && !Number.isNaN(numbers) ? numbers : left.localeCompare(right);
      return direction === "asc" ? compared : -compared;
    });
    if (dispatch) {
      const tr = state.tr.replaceWith(
        table.pos + 1,
        table.pos + 1 + table.node.content.size,
        Fragment.from([...head, ...sorted]),
      );
      // Reordering rows keeps every position valid, but mapping through a
      // whole-table replacement would drop the caret outside the table.
      const { from, to } = state.selection;
      tr.setSelection(
        TextSelection.create(
          tr.doc,
          Math.min(from, tr.doc.content.size),
          Math.min(to, tr.doc.content.size),
        ),
      );
      dispatch(tr);
    }
    return true;
  };
}

export function moveColumn(direction: -1 | 1): Command {
  return (state, dispatch, view) => {
    if (!isInTable(state)) return false;
    const rect = selectedRect(state);
    const target = rect.left + direction;
    if (target < 0 || target >= rect.map.width) return false;
    return moveTableColumn({ from: rect.left, to: target })(state, dispatch, view);
  };
}

export function moveRow(direction: -1 | 1): Command {
  return (state, dispatch, view) => {
    if (!isInTable(state)) return false;
    const rect = selectedRect(state);
    const target = rect.top + direction;
    if (target < 0 || target >= rect.map.height) return false;
    return moveTableRow({ from: rect.top, to: target })(state, dispatch, view);
  };
}

/** Select a whole column or row, which is what opens its grip toolbar. */
export function selectColumn(column: number): Command {
  return (state, dispatch) => {
    if (!isInTable(state)) return false;
    const rect = selectedRect(state);
    const cellPos = rect.map.map[column];
    if (cellPos === undefined) return false;
    if (dispatch) {
      dispatch(
        state.tr.setSelection(
          CellSelection.colSelection(state.doc.resolve(rect.tableStart + cellPos)),
        ),
      );
    }
    return true;
  };
}

export function selectRow(row: number): Command {
  return (state, dispatch) => {
    if (!isInTable(state)) return false;
    const rect = selectedRect(state);
    const cellPos = rect.map.map[row * rect.map.width];
    if (cellPos === undefined) return false;
    if (dispatch) {
      dispatch(
        state.tr.setSelection(
          CellSelection.rowSelection(state.doc.resolve(rect.tableStart + cellPos)),
        ),
      );
    }
    return true;
  };
}

function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** "Export as CSV" (§12). Returns the text; the caller decides how to save it. */
export function tableToCsv(table: ProseMirrorNode): string {
  const lines: string[] = [];
  table.forEach((row) => {
    const cells: string[] = [];
    row.forEach((cell) => cells.push(csvCell(cell.textContent.replace(/\n/g, " ").trim())));
    lines.push(cells.join(","));
  });
  return `${lines.join("\n")}\n`;
}
